<!-- src/components/copySeasonModal.vue -->
<template>
  <div
    class="modal fade"
    :id="`copySeasonModal-${season.id}`"
    tabindex="-1"
    aria-labelledby="copySeasonModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="copySeasonModalLabel">Copy Season</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="copySeason">
            <div class="mb-3">
              <label for="seasonDescription" class="form-label">Name</label>
              <input
                type="text"
                class="form-control"
                id="seasonDescription"
                v-model="description"
                required
                placeholder="Enter season name"
              />
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="seasonStartDate" class="form-label">
                  Start Date
                </label>
                <input
                  type="date"
                  class="form-control"
                  id="seasonStartDate"
                  v-model="startDate"
                  :min="today"
                  required
                />
              </div>
              <div class="col-md-6">
                <label for="seasonEndDate" class="form-label">End Date</label>
                <input
                  type="date"
                  class="form-control"
                  id="seasonEndDate"
                  v-model="endDate"
                  :min="startDate || today"
                  required
                />
              </div>
            </div>
            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="overrideSeason"
                v-model="overrideSeason"
              />
              <label class="form-check-label" for="overrideSeason">
                Override Season
              </label>
            </div>
            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>
            <div class="d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-secondary me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">Create</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  PropType,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { Modal } from 'bootstrap';
import { Season, SeasonBatchSaveRequest, SeasonChannel } from '@/api';
import { seasonBatchApi } from '../apiClient';
import { formatDateToInput } from '@/utils/dateUtils';
import { validateSeasonName } from '@/utils/seasonUtils';

export default defineComponent({
  name: 'CopySeasonModal',
  props: {
    season: {
      type: Object as PropType<Season>,
      required: true,
    },
    courseId: {
      type: Number,
      required: true,
    },
    existingSeasons: {
      type: Array as PropType<Season[]>,
      required: true,
    },
  },
  emits: ['season-copied'],
  setup(props, { emit }) {
    const description = ref('COPY: ' + (props.season.description || ''));

    const startDate = ref(
      props.season.startDate ? formatDateToInput(props.season.startDate) : ''
    );
    const endDate = ref(
      props.season.endDate ? formatDateToInput(props.season.endDate) : ''
    );

    const overrideSeason = ref(false);
    const errorMessage = ref('');

    const today = computed(() => {
      const date = new Date();
      return date.toISOString().split('T')[0];
    });

    const checkDateOverlap = (start: string, end: string) => {
      const newStart = new Date(start).toISOString();
      const newEnd = new Date(end).toISOString();

      return props.existingSeasons.some((season) => {
        const seasonStart = season.startDate
          ? new Date(season.startDate.split('T')[0]).toISOString()
          : null;
        const seasonEnd = season.endDate
          ? new Date(season.endDate.split('T')[0]).toISOString()
          : null;

        if (!seasonStart || !seasonEnd) {
          return false;
        }

        return (
          (newStart >= seasonStart && newStart <= seasonEnd) ||
          (newEnd >= seasonStart && newEnd <= seasonEnd) ||
          (newStart <= seasonStart && newEnd >= seasonEnd)
        );
      });
    };

    const resetModal = () => {
      description.value = 'COPY: ' + (props.season.description || '');
      startDate.value = props.season.startDate
        ? formatDateToInput(props.season.startDate)
        : '';
      endDate.value = props.season.endDate
        ? formatDateToInput(props.season.endDate)
        : '';
      overrideSeason.value = false;
      errorMessage.value = '';
    };

    const handleModalClose = () => {
      setTimeout(resetModal, 300);
    };

    const modalElementId = `copySeasonModal-${props.season.id}`;

    onMounted(() => {
      const modalElement = document.getElementById(modalElementId);
      if (modalElement) {
        modalElement.addEventListener('hidden.bs.modal', handleModalClose);
      }
    });

    onBeforeUnmount(() => {
      const modalElement = document.getElementById(modalElementId);
      if (modalElement) {
        modalElement.removeEventListener('hidden.bs.modal', handleModalClose);
      }
    });

    const validateDates = () => {
      // Always perform these validations
      if (startDate.value < today.value) {
        errorMessage.value = 'Start date cannot be in the past.';
        return false;
      }
      if (endDate.value < startDate.value) {
        errorMessage.value = 'End date cannot be before the start date.';
        return false;
      }

      // Only perform overlap check if overrideSeason is not checked
      if (
        !overrideSeason.value &&
        checkDateOverlap(startDate.value, endDate.value)
      ) {
        errorMessage.value =
          'The selected dates overlap with an existing season.';
        return false;
      }

      errorMessage.value = '';
      return true;
    };

    const copySeason = async () => {
      if (
        !validateDates() ||
        !validateSeasonName(
          description.value,
          props.existingSeasons,
          errorMessage
        )
      ) {
        return;
      }

      try {
        // Construct the SeasonBatchSaveRequest
        const newSeason: Partial<Season> = {
          // Omit 'id' to indicate a new season
          description: description.value,
          startDate: startDate.value,
          endDate: endDate.value,
          priority: overrideSeason.value ? 1 : 0, // Set priority based on checkbox
          seasonChannels: props.season.seasonChannels?.map(
            (seasonChannel: SeasonChannel) => ({
              channelId: seasonChannel.channel?.id ?? 0,
              strategyId: seasonChannel.strategyId,
              usePercentage: seasonChannel.usePercentage,
              percentAdjust: seasonChannel.percentAdjust,
              amountAdjust: seasonChannel.amountAdjust,
            })
          ),
        };

        const seasonBatchSaveRequest: SeasonBatchSaveRequest = {
          editedSeasons: [newSeason],
          deletedSeasonIds: [],
        };

        // Send the batch save request
        const response = await seasonBatchApi.seasonBatchSavePut(
          props.courseId,
          seasonBatchSaveRequest
        );

        if (response.status === 200) {
          console.log('Season Copied Successfully:', response.data);

          // Emit event to notify parent component
          emit('season-copied', response.data);

          resetModal();

          // Hide the modal
          const modalElement = document.getElementById(modalElementId);
          if (modalElement) {
            const bootstrapModal =
              Modal.getInstance(modalElement) || new Modal(modalElement);
            bootstrapModal.hide();
          } else {
            console.error(`Modal element with id ${modalElementId} not found`);
          }
        } else {
          console.error('Error copying season:', response);
          // Optionally, emit an error event or display a notification
          errorMessage.value = 'Failed to copy the season. Please try again.';
        }
      } catch (error) {
        console.error('Error copying season:', error);
        // Optionally, emit an error event or display a notification
        errorMessage.value =
          'An unexpected error occurred while copying the season.';
      }
    };

    return {
      description,
      startDate,
      endDate,
      overrideSeason,
      copySeason,
      errorMessage,
      today,
      checkDateOverlap,
      validateDates,
    };
  },
});
</script>

<style scoped>
.modal-dialog-centered {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.modal-content {
  border-radius: 8px;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
}

.form-label {
  display: block;
  text-align: left;
}

.form-check-label {
  margin-left: 5px;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.alert {
  margin-top: 10px;
}
</style>
